import { Injectable } from '@angular/core';

import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import firebase from 'firebase/compat';

import { Observable } from 'rxjs';

import { Message } from '../models/message';
import { TourStop } from '../models/tour-stop';
import { GeoLocation } from '../models/geo-location';
import { Tour } from '../models/tour';
import { AppAuth } from '../models/appauth';
import { ItemData } from '../models/item-data';
import { TransportPlanData } from '../models/transport-plan-data';
import { Transport } from '../models/transport';
import { ItemStatus } from '../models/item-status';
import { HomeDelivery } from '../models/home-delivery';
import { AError } from '../models/aerror';
import { FunctionsResponse } from '../models/functions-response';
import { FunctionsRequest } from '../models/functions-request';

import { EventService } from './event.service';
import { DeviceService } from './device.service';
import { ConfigService } from './config.service';
import { extractFirstNameAndLastName } from '../utils';
import { ItemsByCategory } from '../models/items-by-category';
import { ContractStatus } from "../models/contract-status";

type TransportStatus = { pickupItemStatus: ItemStatus, handoverItemStatus: ItemStatus };

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  private appAuth: AppAuth;

  constructor(
    private fns: AngularFireFunctions,
    private auth: AngularFireAuth,
    private device: DeviceService,
    private config: ConfigService,
    private events: EventService
  ) {
    this.auth.user.subscribe(u => {
      if (u) {
        this.updateAppAuthFromUser(u);
      } else {
        this.clearAppAuth();
      }
    });
    this.events.phoneNumberChanged.subscribe(value => {
      if (this.appAuth) {
        if (value) {
          this.appAuth.phoneNumber = value;
          console.log(`Phone number = ${value}`);
        } else {
          this.appAuth.phoneNumber = null;
          console.log(`Phone number cleared`);
        }
      }
    });
  }

  private updateAppAuthFromUser(user: firebase.User): void {
    const displayNameComponents = extractFirstNameAndLastName(user.displayName);
    let firstName = displayNameComponents ? displayNameComponents[0] : 'None';
    let lastName = displayNameComponents ? displayNameComponents[1] : 'None';
    if (displayNameComponents && displayNameComponents.length >= 2) {
      firstName = displayNameComponents[0];
      lastName = displayNameComponents[1];
    }
    this.appAuth = {
      deviceId: this.device.deviceId,
      userName: user.email,
      firstName,
      lastName,
      phoneNumber: this.config.getPhoneNumber(),
      acceptLanguage: navigator.language.substr(0, 2)
    };
    console.log(`App authentication established for user: ${user.email}`);
  }

  private clearAppAuth(): void {
    console.log(`Clearing app authentication`);
    this.appAuth = undefined;
  }

  /**
   * Obtain a typed reference to the Firebase Function callable.
   * @param name The name of the callable.
   */
  private typedCallable(name: string): (data: FunctionsRequest) => Observable<FunctionsResponse> {
    return this.fns.httpsCallable<FunctionsRequest, FunctionsResponse>(name);
  }

  /**
   * Get unread messages (= notifications)
   */
  getUnreadMessages(): Promise<Message[]> {
    return this.typedCallable('rubicon-getUnreadMessages')({
      appAuth: this.appAuth
    }).toPromise()
      .then(response => this.handleAPIError(response))
      .then(response => {
        if (response.jsonBody && (response.jsonBody instanceof Array)) {
          return Promise.resolve(response.jsonBody.map(jsonMessage => Message.fromJSON(jsonMessage)));
        } else {
          return Promise.reject(AError.wrongResponse(response));
        }
      });
  }

  /**
   * Mark a message as read
   */
  markMessageAsRead(message: Message): Promise<void> {
    if (!message || !message.id) {
      return Promise.reject(AError.wrongRequest('`message` must be specified and have an ID to mark it as read'));
    }
    return this.typedCallable('rubicon-markMessageAsRead')({
      messageId: message.id,
      appAuth: this.appAuth
    }).toPromise()
      .then(response => this.handleAPIError(response))
      .then(response => {
        if (response.statusCode === 204) {
          return Promise.resolve();
        } else {
          return Promise.reject(AError.wrongResponse(response));
        }
      });
  }

  /**
   * Scan a transport item
   *
   * @param barcode Barcode
   * @param transport Transport
   * @param tour Tour
   * @param location The user's current location
   * @returns Promise resolving to the latest transport status
   */
  scanTransportItem(barcode: string, transport: Transport, tour: Tour, location: GeoLocation): Promise<TransportStatus> {
    if (!barcode || !transport || !tour || !location || location.latitude === undefined || location.longitude === undefined) {
      return Promise.reject(
        AError.wrongRequest('A barcode, a transport, a tour and a location is required to get the status of a transport'));
    }

    return this.typedCallable('rubicon-scanTransportCodeItem')({
      barcode,
      transportCodeId: transport.id,
      tourId: tour.id,
      geoLocation: location,
      appAuth: this.appAuth
    }).toPromise()
      .then(response => this.handleAPIError(response))
      .then(response => {
        if (response.jsonBody && response.jsonBody.pickupItemStatus && response.jsonBody.handoverItemStatus) {
          return Promise.resolve({
            pickupItemStatus: ItemStatus.fromJSON(response.jsonBody.pickupItemStatus),
            handoverItemStatus: ItemStatus.fromJSON(response.jsonBody.handoverItemStatus),
          });
        } else {
          return Promise.reject(AError.wrongResponse(response));
        }
      });
  }

  /**
   * Scan a home delivery item
   *
   * @param barcode Barcode to be scanned
   * @param homeDelivery Home delivery that the item belongs to
   * @param tour Tour that the home delivery is included in
   * @param location The user's current location
   * @returns Promise resolving to the new contract status of the home delivery
   */
  scanHomeDeliveryItem(barcode: string, homeDelivery: HomeDelivery, tour: Tour, location: GeoLocation): Promise<ContractStatus> {
    if (!barcode || !homeDelivery || !tour || !location) {
      return Promise.reject(AError.wrongRequest(
        'A barcode, a home delivery, a tour and a location are required to scan a home delivery item'));
    }
    return this.typedCallable('rubicon-scanHomeDeliveryItem')({
      tourId: tour.id,
      homeDeliveryContractNumber: homeDelivery.contractNumber,
      deliveryType: homeDelivery.kind,
      barcode,
      geoLocation: location,
      appAuth: this.appAuth
    }).toPromise()
      .then(response => this.handleAPIError(response))
      .then(response => {
        if (response.jsonBody) {
          return Promise.resolve(ContractStatus.fromJSON(response.jsonBody));
        } else {
          return Promise.reject(AError.wrongResponse(response));
        }
      });
  }

  /**
   * Start a transport
   *
   * @param barcode Transport barcode
   * @param tour Tour
   * @param location The user's current locaiton
   * @returns Promise resolving with the updated transport
   */
  startTransport(barcode: string, tour: Tour, location: GeoLocation): Promise<Transport> {
    if (!barcode || !tour || !tour.id || !location || location.latitude === undefined || location.longitude === undefined) {
      return Promise.reject(AError.wrongRequest('A barcode, a tour and a location is required to start a transport'));
    }
    return this.typedCallable('rubicon-startTransport')({
      tourId: tour.id,
      barcode,
      geoLocation: location,
      appAuth: this.appAuth
    }).toPromise()
      .then(response => this.handleAPIError(response))
      .then(response => {
        if (response.jsonBody && response.jsonBody.transportCode) {
          return Promise.resolve(Transport.fromJSON(response.jsonBody.transportCode));
        } else {
          return Promise.reject(AError.wrongResponse(response));
        }
      });
  }

  /**
   * Get the status of a transport.
   *
   * Returns the item status for both pickup and handover.
   *
   * @param transport Transport
   * @param tour Tour
   * @returns Promise resolving to the status
   */
  getTransportStatus(transport: Transport, tour: Tour): Promise<TransportStatus> {
    if (!transport || !tour) {
      return Promise.reject(AError.wrongRequest('A transport and a tour is required to get the status of a transport'));
    }
    return this.typedCallable('rubicon-getTransportStatus')({
      tourId: tour.id,
      transportCodeId: transport.id,
      appAuth: this.appAuth
    }).toPromise()
      .then(response => this.handleAPIError(response))
      .then(response => {
        if (response.jsonBody
          && response.jsonBody.pickupItemStatus
          && response.jsonBody.handoverItemStatus) {
          return Promise.resolve({
            pickupItemStatus: ItemStatus.fromJSON(response.jsonBody.pickupItemStatus),
            handoverItemStatus: ItemStatus.fromJSON(response.jsonBody.handoverItemStatus)
          });
        } else {
          return Promise.reject(AError.wrongResponse(response));
        }
      });
  }

  /**
   * Finish a transport
   *
   * @param transport The transport to finish
   * @param tour The tour that includes this transport
   * @returns Promise resolving successfully when finishing is successful
   */
  finishTransport(transport: Transport, tour: Tour): Promise<void> {
    if (!transport || !tour) {
      return Promise.reject(AError.wrongRequest('A transport and a tour are both required to finish a transport'));
    }
    return this.typedCallable('rubicon-finishTransport')({
      tourId: tour.id,
      transportCodeId: transport.id,
      appAuth: this.appAuth
    }).toPromise()
      .then(response => this.handleAPIError(response))
      .then(response => {
        if (response.statusCode === 204) {
          return Promise.resolve();
        } else {
          return Promise.reject(AError.wrongResponse(response));
        }
      });
  }

  /**
   * Start a tour based on a tour start barcode
   *
   * @param barcode Barcode to start the tour with
   * @param location User's current location
   * @returns Promise resolving to the tour that was started with the start barcode
   */
  startTour(barcode: string, location: GeoLocation): Promise<Tour> {
    if (!barcode || !location || location.latitude === undefined || location.longitude === undefined) {
      return Promise.reject(AError.wrongRequest('A barcode and a location is required to start a tour'));
    }
    return this.typedCallable('rubicon-startTour')({
      barcode,
      geoLocation: location,
      appAuth: this.appAuth
    }).toPromise()
      .then(response => this.handleAPIError(response))
      .then(response => {
        if (response.jsonBody &&
          response.jsonBody.tour &&
          response.jsonBody.tour.id &&
          response.jsonBody.tour.name &&
          response.jsonBody.tour.endBarcode) {
          return Promise.resolve(Tour.fromJSON(response.jsonBody.tour));
        } else {
          return Promise.reject(AError.wrongResponse(response));
        }
      });
  }

  /**
   * Get tour stops for a given tour
   *
   * @param tour The tour to get the tour stops for
   * @returns Promise resolving to a list of tour stops
   */
  getTourStopsAndItems(tour: Tour): Promise<[TourStop[], ItemsByCategory[]]> {
    if (!tour || !tour.id) {
      return Promise.reject(AError.wrongRequest('A tour, including an ID, is required to get the tour status'));
    }
    return this.typedCallable('rubicon-getTourStatus')({
      tourId: tour.id,
      appAuth: this.appAuth
    }).toPromise()
      .then(response => this.handleAPIError(response))
      .then(response => {
        if (response.jsonBody
            && response.jsonBody.tourStops && response.jsonBody.tourStops instanceof Array
            && response.jsonBody.items && response.jsonBody.items instanceof Array) {
          const result: [TourStop[], ItemsByCategory[]] = [
            response.jsonBody.tourStops.map(tourStopJson => TourStop.fromJSON(tourStopJson)),
            response.jsonBody.items.map(item => ItemsByCategory.fromJSON(item))
          ];
          return Promise.resolve(result);
        } else {
          return Promise.reject(AError.wrongResponse(response));
        }
      });
  }

  /**
   * Finish a tour with an end barcode
   *
   * @param endBarcode The end barcode to finish the tour wiht
   * @param tour The tour to finish
   * @param location The user's current location
   * @returns Promise resolving successfully if ending the tour was successful
   */
  finishTour(endBarcode: string, tour: Tour, location: GeoLocation): Promise<void> {
    if (!endBarcode || !tour || !tour.id || !location || location.latitude === undefined || location.longitude === undefined) {
      return Promise.reject(AError.wrongRequest('An end barcode, a tour and a location is required to finish a tour'));
    }
    return this.typedCallable('rubicon-finishTour')({
      tourId: tour.id,
      barcode: endBarcode,
      geoLocation: location,
      appAuth: this.appAuth
    }).toPromise()
      .then(response => this.handleAPIError(response))
      .then(response => {
        if (response.statusCode === 204) {
          return Promise.resolve();
        } else {
          return Promise.reject(AError.wrongResponse(response));
        }
      });
  }

  /**
   * Get the status of a home delivery
   *
   * @param homeDelivery The home delivery to get the status of
   * @param tour The tour that contains the home delivery
   * @returns Promise resolving with the status
   */
  getHomeDeliveryStatus(homeDelivery: HomeDelivery, tour: Tour): Promise<ItemStatus> {
    if (!homeDelivery || !tour) {
      return Promise.reject(AError.wrongRequest('A home delivery and a tour is required to get the status of a home delivery'));
    }
    return this.typedCallable('rubicon-getHomeDeliveryStatus')({
      tourId: tour.id,
      homeDeliveryContractNumber: homeDelivery.contractNumber,
      deliveryType: homeDelivery.kind,
      appAuth: this.appAuth
    }).toPromise()
      .then(response => this.handleAPIError(response))
      .then(response => {
        if (response.jsonBody && response.jsonBody.itemStatus) {
          return Promise.resolve(ItemStatus.fromJSON(response.jsonBody.itemStatus));
        } else {
          return Promise.reject(AError.wrongResponse(response));
        }
      });
  }

  scanItemTransportPlan(barcode: string, location: GeoLocation): Promise<[ItemData, TransportPlanData]> {
    if (!barcode || !location || location.latitude === undefined || location.longitude === undefined) {
      return Promise.reject(AError.wrongRequest('A barcode and a location is required to get a transport plan'));
    }
    return this.typedCallable('rubicon-scanTransportPlan')({
      barcode,
      geoLocation: location,
      appAuth: this.appAuth
    }).toPromise()
      .then(response => this.handleAPIError(response))
      .then(response => {
        if (response.jsonBody && response.jsonBody.item && response.jsonBody.transportPlan) {
          const result: [ItemData, TransportPlanData] = [
            ItemData.fromJSON(response.jsonBody.item),
            TransportPlanData.fromJSON(response.jsonBody.transportPlan)];
          return Promise.resolve(result);
        } else {
          return Promise.reject(AError.wrongResponse(response));
        }
      });
  }

  // Extension: confirm that less items than expected were scanned

  /**
   * Explicitly finish a home delivery. This is used when less items than were scanned than expected (Mindermenge)
   * @param homeDelivery The home delivery to finish
   * @param tour The tour that contains the home delivery
   */
  finishHomeDelivery(homeDelivery: HomeDelivery, tour: Tour): Promise<void> {
    if (!homeDelivery || !homeDelivery.contractNumber || !homeDelivery.kind || !tour || !tour.id) {
      return Promise.reject(AError.wrongRequest('An home delivery and a tour is required to finish a home delivery'));
    }
    return this.typedCallable('rubicon-finishHomeDelivery')({
      tourId: tour.id,
      homeDeliveryContractNumber: homeDelivery.contractNumber,
      deliveryType: homeDelivery.kind,
      appAuth: this.appAuth
    }).toPromise()
      .then(response => this.handleAPIError(response))
      .then(response => {
        if (response.statusCode === 204) {
          return Promise.resolve();
        } else {
          return Promise.reject(AError.wrongResponse(response));
        }
      });
  }

  // Extension: increase item count in home delivery
  increaseHomeDeliveryItemCount(homeDelivery: HomeDelivery, tour: Tour, additionalItemCount: number): Promise<ItemStatus> {
    if (!homeDelivery || !homeDelivery.contractNumber || !homeDelivery.kind || !tour || !tour.id || !additionalItemCount) {
      return Promise.reject(AError.wrongRequest('An home delivery, a tour and a positive additional item count is required increase the item count'));
    }
    return this.typedCallable('rubicon-increaseHomeDeliveryItemCount')({
      tourId: tour.id,
      homeDeliveryContractNumber: homeDelivery.contractNumber,
      deliveryType: homeDelivery.kind,
      additionalItemCount,
      appAuth: this.appAuth
    }).toPromise()
      .then(response => this.handleAPIError(response))
      .then(response => {
        if (response.jsonBody && response.jsonBody.itemStatus) {
          return Promise.resolve(ItemStatus.fromJSON(response.jsonBody.itemStatus));
        } else {
          return Promise.reject(AError.wrongResponse(response));
        }
      });
  }

  // Extension: batch home delivery scan
  batchScanHomeDelivery(barcode: string, homeDelivery: HomeDelivery, tour: Tour, location: GeoLocation, itemCount: number): Promise<ContractStatus> {
    if (!homeDelivery || !homeDelivery.contractNumber || !homeDelivery.kind || !tour || !tour.id || itemCount <= 0) {
      return Promise.reject(AError.wrongRequest('A home delivery, a tour and a actual item count is required for batch scanning'));
    }
    return this.typedCallable('rubicon-batchScanHomeDeliveryItem')({
      deliveryType: homeDelivery.kind,
      contractNumber: homeDelivery.contractNumber,
      actualNumberOfItems: itemCount,
      tourId: tour.id,
      barcode: barcode,
      geoLocation: location,
      appAuth: this.appAuth
    }).toPromise()
      .then(response => this.handleAPIError(response))
      .then(response => {
        if (response.jsonBody && response.jsonBody.itemStatus) {
          return Promise.resolve(ContractStatus.fromJSON(response.jsonBody));
        } else {
          return Promise.reject(AError.wrongResponse(response));
        }
      });
  }

  private handleAPIError(response: FunctionsResponse): Promise<FunctionsResponse> {
    if (response.statusCode >= 200 && response.statusCode < 400) {
      return Promise.resolve(response);
    } else {
      return Promise.reject(AError.fromAPIError(response));
    }
  }
}
